import { useEffect } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MainLayout } from '@/layouts/MainLayout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { OfflineIndicator } from '@/components/OfflineIndicator'
import { InstallPrompt } from '@/components/InstallPrompt'
import { Login, Dashboard, Tasks, TimeLogs, Clients, NotificationSettings } from '@/pages'
import { useAuthStore } from '@/stores/authStore'
import { useTimerStore } from '@/stores/timerStore'
import { useAgentEventStream } from '@/hooks/useAgentEventStream'
import { AgentEventToast } from '@/components/AgentEventToast'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

function AppContent() {
  const { isRunning, tick, hydrate } = useTimerStore()
  useAgentEventStream()

  // On mount, attempt to restore session from refresh token in localStorage
  useEffect(() => {
    useAuthStore.getState().restoreSession()
  }, [])

  // Hydrate timer from localStorage on mount
  useEffect(() => {
    hydrate()
  }, [hydrate])

  // Timer tick effect
  useEffect(() => {
    if (!isRunning) return

    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [isRunning, tick])

  return (
    <>
      {/* Global offline/sync indicator */}
      <OfflineIndicator />
      <AgentEventToast />

      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/time" element={<TimeLogs />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/calendar" element={<PlaceholderPage title="Calendar" />} />
          <Route path="/settings" element={<PlaceholderPage title="Settings" />} />
          <Route path="/settings/notifications" element={<NotificationSettings />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* PWA install prompt */}
      <InstallPrompt />
    </>
  )
}

// Placeholder for pages not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="text-muted-foreground">
        This page will be implemented in a future phase.
      </p>
    </div>
  )
}

// 404 page
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-2">404</h1>
        <p className="text-muted-foreground">Page not found</p>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
